import { motion } from "motion/react"
import MeshGradient from "./MeshGradient"

interface HeroProps {
  title_line1: string
  title_line2: string
  subtitle: string
  cta_discover: string
  cta_start_project: string
}

export default function Hero({
  title_line1,
  title_line2,
  subtitle,
  cta_discover,
  cta_start_project,
}: HeroProps) {
  const prefersReducedMotion =
    typeof window !== "undefined"
      ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
      : false

  const handleAnchorClick = (href: string) => {
    const target = document.querySelector(href)
    if (target) {
      const top = target.getBoundingClientRect().top + window.scrollY - 64
      window.scrollTo({ top, behavior: "smooth" })
    }
  }

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-[#0a0a0f]">
      <MeshGradient />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        {/* Badge */}
        <motion.div
          initial={prefersReducedMotion ? false : { opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="inline-flex items-center gap-2 px-3 py-1 mb-8 text-xs font-medium text-violet-300 bg-violet-500/10 border border-violet-500/20 rounded-full"
        >
          <span className="w-1.5 h-1.5 bg-violet-400 rounded-full animate-pulse" />
          Agence Web — Nantes & Paris
        </motion.div>

        {/* Headline */}
        <div className="mb-6 overflow-hidden">
          <motion.h1
            initial={prefersReducedMotion ? false : { opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="text-5xl sm:text-7xl lg:text-8xl font-bold tracking-tight text-white leading-[1.05]"
          >
            {title_line1}
            <br />
            <span className="bg-gradient-to-r from-violet-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">
              {title_line2}
            </span>
          </motion.h1>
        </div>

        {/* Subtitle */}
        <motion.p
          initial={prefersReducedMotion ? false : { opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="max-w-2xl mx-auto mb-10 text-lg text-slate-400 leading-relaxed"
        >
          {subtitle}
        </motion.p>

        {/* CTAs */}
        <motion.div
          initial={prefersReducedMotion ? false : { opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="flex flex-col sm:flex-row gap-4 justify-center"
        >
          <button
            onClick={() => handleAnchorClick("#clients")}
            className="px-8 py-4 text-sm font-medium text-white bg-violet-600 hover:bg-violet-500 rounded-xl transition-all duration-200 hover:scale-105 hover:shadow-lg hover:shadow-violet-500/25"
          >
            {cta_discover}
          </button>
          <button
            onClick={() => handleAnchorClick("#contact")}
            className="px-8 py-4 text-sm font-medium text-white bg-white/10 hover:bg-white/15 border border-white/10 rounded-xl backdrop-blur-sm transition-all duration-200 hover:scale-105"
          >
            {cta_start_project}
          </button>
        </motion.div>

      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={prefersReducedMotion ? false : { opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.2 }}
        className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2"
      >
        <span className="text-xs text-slate-500 tracking-widest uppercase">Scroll</span>
        <motion.div
          animate={prefersReducedMotion ? {} : { y: [0, 8, 0] }}
          transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
          className="w-px h-8 bg-gradient-to-b from-slate-500 to-transparent"
        />
      </motion.div>
    </section>
  )
}
